import { Navigate } from "@tanstack/react-router";
import { useFeature, type ClientFeatureKey } from "@/hooks/useFeatures";

interface FeatureGateProps {
  feature: ClientFeatureKey;
  /** Where the reader was headed, for the restricted page's console note. */
  from: string;
  children: React.ReactNode;
}

/**
 * A page an organization has not been given (T-1106).
 *
 * Hiding the nav entry is only half of it — the URL still exists, and typing it
 * would load a board whose every request the API refuses, turning one missing
 * feature into a screenful of failed calls. Nothing renders until the answer is
 * in, for the same reason: a board that appears and is then taken away is worse
 * than a moment of nothing.
 */
export function FeatureGate({ feature, from, children }: FeatureGateProps) {
  const { enabled, isLoading } = useFeature(feature);

  if (isLoading) return null;

  if (!enabled) {
    return <Navigate to="/unauthorized" search={{ reason: "feature_unavailable", from }} replace />;
  }

  return <>{children}</>;
}
